import merge from "lodash.merge";
import { getContext, setContext } from "svelte";
import { writable, type Writable, type Subscriber } from "svelte/store";
import { setCookie } from "../utils/cookie";
import cfg from "$lib/config/config";

export const key = Symbol();

export type RegionInfo = {
  region: string;
  country: string | undefined;
  locale: string | undefined;
};

export class RegionStore {
  private _regionInfo: Writable<RegionInfo>;
  constructor(
    initialState: RegionInfo = {
      region: cfg.REGION_ID,
      country: undefined,
      locale: undefined,
    },
  ) {
    this._regionInfo = writable<RegionInfo>(initialState);
  }

  subscribe(run: Subscriber<RegionInfo>) {
    return this._regionInfo.subscribe(run);
  }
  init(v: Partial<RegionInfo>) {
    this._regionInfo.update(currentRegionInfo =>
      merge({}, currentRegionInfo, v),
    );
  }
  set(v: Partial<RegionInfo>) {
    if (v && v.region) {
      setCookie("region", v.region);
    }
    if (v && v.country) {
      setCookie("country", v.country);
    }
    if (v && v.locale) {
      setCookie("locale", v.locale);
    }

    // XXX: Using `update` here instead of `set` to avoid `null`/`undefined`
    // values of `v.locale` clobbering an existing valid value in the store
    // XXX: Using `merge` here so that `undefined` value of `v.locale` does
    // not clobber an existing valid value in the store when using the
    // spread pattern (as seen elsewhere in this module)
    this._regionInfo.update(currentRegionInfo =>
      merge({}, currentRegionInfo, v),
    );
  }
  updateRegion(newRegion: string) {
    setCookie("region", newRegion);
    this._regionInfo.update(currentRegionInfo => {
      return {
        ...currentRegionInfo,
        region: newRegion,
      };
    });
  }
  updateCountry(newCountry: string) {
    setCookie("country", newCountry);
    this._regionInfo.update(currentRegionInfo => {
      return {
        ...currentRegionInfo,
        country: newCountry,
      };
    });
  }
  updateLocale(newLocale: string) {
    setCookie("locale", newLocale);
    this._regionInfo.update(currentRegionInfo => {
      return {
        ...currentRegionInfo,
        locale: newLocale,
      };
    });
  }
}

export function createRegionStore(initialState: RegionInfo) {
  const regionStore = new RegionStore(initialState);
  setContext(key, regionStore);
  return regionStore;
}

export function regionStore(): Writable<RegionInfo> {
  const regionInfo = getContext<Writable<RegionInfo>>(key);
  if (!regionInfo) {
    throw new Error(
      `region store not initialised; call createRegionStore before accessing the store`,
    );
  }
  return regionInfo;
}
